import React from 'react';

import { asset } from '../../../../lib/asset';
import { Root, Screen, TopBar, BottomBar, Content, Zoom, Overlay } from './PhoneFrame.styles';

/** Screen corner radius as a fraction of the frame width (so it scales). */
const RADIUS_RATIO = 0.112;

export type PhoneFrameProps = {
  children: React.ReactNode;
  /** Background for the status-bar strip behind the notch (--top-nav-surface). */
  topColor?: string;
  /** Background for the home-indicator strip (--bottom-nav-surface). */
  bottomColor?: string;
  /**
   * When set, render the children at this virtual viewport width and scale them
   * down to fit the screen — zooming the mobile page out so more of the
   * responsive layout is visible. Omit to fill the screen 1:1.
   */
  zoomWidth?: number;
  /** Which dimension drives the size: parent height (default) or parent width. */
  fit?: 'height' | 'width';
  /** Optional element layered over the entire screen cut-out (e.g. a loading
   *  placeholder) — sits above the children and the status/home strips. */
  overlay?: React.ReactNode;
};

/**
 * iPhone device frame. The frame is a PNG with a transparent screen cut-out laid
 * over the content, so the children show through the screen while the bezel /
 * notch render on top.
 */
export const PhoneFrame = ({
  children,
  topColor,
  bottomColor,
  zoomWidth,
  fit = 'height',
  overlay,
}: PhoneFrameProps): React.ReactElement => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [radius, setRadius] = React.useState(42);
  const [scale, setScale] = React.useState(0);

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const measure = (): void => {
      setRadius(root.offsetWidth * RADIUS_RATIO);
      if (zoomWidth && contentRef.current) {
        setScale(contentRef.current.offsetWidth / zoomWidth);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [zoomWidth]);

  const zoomHeight =
    scale && contentRef.current ? `${contentRef.current.offsetHeight / scale}px` : '100%';

  return (
    <Root ref={rootRef} $fit={fit}>
      <Screen $radius={radius} $bg={topColor}>
        <TopBar $color={topColor} />
        <Content ref={contentRef}>
          {zoomWidth ? (
            <Zoom $width={zoomWidth} $height={zoomHeight} $scale={scale || 0.0001}>
              {children}
            </Zoom>
          ) : (
            children
          )}
        </Content>
        <BottomBar $color={bottomColor} />
        {overlay}
      </Screen>

      <Overlay src={asset('phone-frame.png')} alt="" />
    </Root>
  );
};

export default PhoneFrame;
